import { useState, useEffect } from "react";
import { Check, Zap, Crown, ArrowRight, Star, Sparkles } from "lucide-react";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { getPlans, getUserSubscription, createOrder, verifyPayment, getCreditPlans } from "../apis/subscription";
import { loadScript } from "../utils/razorpay";

const SubscriptionInline = ({ onSuccess }) => {
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const [trialPlan, setTrialPlan] = useState(null);
  const [creditPlans, setCreditPlans] = useState([]);

  useEffect(() => {
    loadPlans();
    loadCurrentSubscription();
    loadCreditPlans();
  }, []);

  const loadCreditPlans = async () => {
    try {
      const response = await getCreditPlans();
      if (response.data.success) {
        setCreditPlans(response.data.data);
      }
    } catch (error) {
      console.error("Error loading credit plans:", error);
    }
  };

  const loadPlans = async () => {
    try {
      setIsLoading(true);
      const response = await getPlans();
      if (response.data.success) {
        const allPlans = response.data.data;
        const hasUsedTrialValue = response.data.has_used_trial || false;
        setHasUsedTrial(hasUsedTrialValue);
        
        const trial = allPlans.find(p => p.plan_name === 'trial');
        const regularPlans = allPlans.filter(p => p.plan_name !== 'trial');
        
        setTrialPlan(trial);
        setPlans(regularPlans);
      }
    } catch (error) {
      console.error("Error loading plans:", error);
      toast.error("Failed to load subscription plans");
    } finally {
      setIsLoading(false);
    }
  };

  const loadCurrentSubscription = async () => {
    try {
      const response = await getUserSubscription();
      if (response.data.success && response.data.data) {
        const subscription = response.data.data;
        const expiresAt = new Date(subscription.expires_at);
        const isExpired = expiresAt < new Date();
        
        if (!isExpired) {
          setCurrentSubscription(subscription);
        } else {
          setCurrentSubscription(null);
        }
      } else {
        setCurrentSubscription(null);
      }
    } catch (error) {
      console.error("Error loading subscription:", error);
      setCurrentSubscription(null);
    }
  };

  const getDisplayPrice = (plan, period) => {
    if (period === 'yearly') {
      const monthly = parseFloat(plan.monthly_amount || plan.amount);
      const discount = parseFloat(plan.yearly_discount || 0);
      return monthly - discount;
    }
    return parseFloat(plan.monthly_amount || plan.amount);
  };

  const getPaymentAmount = (plan, period) => {
    if (period === 'yearly') {
      const monthly = parseFloat(plan.monthly_amount || plan.amount);
      const discount = parseFloat(plan.yearly_discount || 0);
      const discountedMonthly = monthly - discount;
      return discountedMonthly * 12;
    }
    return parseFloat(plan.monthly_amount || plan.amount);
  };

  const handleSubscribe = async (plan) => {
    setProcessingPlan(plan.plan_name);

    try {
      await loadScript();

      const paymentAmount = getPaymentAmount(plan, billingPeriod);
      
      const orderResponse = await createOrder(plan.plan_name, billingPeriod, paymentAmount);
      if (!orderResponse.data.success) {
        throw new Error(orderResponse.data.error || "Failed to create order");
      }

      const { order_id, amount, key_id } = orderResponse.data.data;

      const options = {
        key: key_id,
        amount: amount,
        currency: "INR",
        name: "Blutec PING",
        description: `Subscribe to ${plan.plan_display_name}`,
        order_id: order_id,
        handler: async function (response) {
          try {
            const verifyResponse = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (verifyResponse.data.success) {
              toast.success("Subscription activated successfully!");
              await loadCurrentSubscription();
              if (onSuccess) {
                onSuccess(verifyResponse.data.data.subscription);
              }
              window.location.reload();
            } else {
              throw new Error(verifyResponse.data.error || "Payment verification failed");
            }
          } catch (error) {
            console.error("Payment verification error:", error);
            toast.error(error.response?.data?.error || "Payment verification failed");
          } finally {
            setProcessingPlan(null);
          }
        },
        prefill: {
          name: JSON.parse(localStorage.getItem("user") || "{}").name || "",
          email: JSON.parse(localStorage.getItem("user") || "{}").email || "",
        },
        theme: {
          color: "#048AAD",
        },
        modal: {
          ondismiss: function () {
            setProcessingPlan(null);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error("Subscribe error:", error);
      toast.error(error.response?.data?.error || "Failed to initiate payment");
      setProcessingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Subscription Required</h1>
          <p className="text-lg text-gray-600">Choose a subscription plan to access this feature</p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-gray-900 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Trial Plan Banner - Show on top if available and not used */}
            {trialPlan && !hasUsedTrial && !currentSubscription && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 rounded-2xl p-6 shadow-lg border border-blue-500"
              >
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex-1 text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
                      <Star size={20} className="text-white" />
                      <h3 className="text-2xl font-bold text-white">Start with Trial</h3>
                    </div>
                    <p className="text-white/90 text-base mb-2 font-medium">
                      Try all features for ₹500 - One month full access!
                    </p>
                    {trialPlan?.ai_credits > 0 && (() => {
                      const trialCreditPlan = trialPlan.ai_credit_plan_id 
                        ? creditPlans.find(cp => cp.id === trialPlan.ai_credit_plan_id)
                        : null;
                      const trialTotalReplies = trialCreditPlan 
                        ? trialPlan.ai_credits * trialCreditPlan.per_credit_ai_replies
                        : 0;
                      return trialCreditPlan ? (
                        <p className="text-white/90 text-sm mb-2 font-medium flex items-center gap-2">
                          <Sparkles size={16} className="text-white" />
                          Includes {trialPlan.ai_credits} AI Credits ({trialPlan.ai_credits} credits × {trialCreditPlan.per_credit_ai_replies} replies = {trialTotalReplies} AI replies)
                        </p>
                      ) : null;
                    })()}
                    <p className="text-white/80 text-sm font-medium">
                      Perfect for testing • One-time use only
                    </p>
                  </div>
                  <motion.button
                    onClick={() => handleSubscribe(trialPlan)}
                    disabled={processingPlan === 'trial'}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-8 py-4 bg-white text-blue-600 rounded-xl font-bold text-base hover:bg-gray-100 transition-all shadow-lg flex items-center gap-2"
                  >
                    {processingPlan === 'trial' ? (
                      <>
                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Get Trial - ₹500
                        <ArrowRight size={20} />
                      </>
                    )}
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className={`text-lg font-bold ${billingPeriod === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly');
                }}
                className={`relative inline-flex h-10 w-16 items-center rounded-full transition-colors cursor-pointer ${
                  billingPeriod === 'yearly' ? 'bg-gray-900' : 'bg-gray-300'
                }`}
                aria-label="Toggle billing period"
              >
                <span
                  className={`inline-block h-8 w-8 transform rounded-full bg-white transition-transform shadow-md ${
                    billingPeriod === 'yearly' ? 'translate-x-8' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-lg font-bold ${billingPeriod === 'yearly' ? 'text-gray-900' : 'text-gray-500'}`}>
                Yearly
              </span>
              {billingPeriod === 'yearly' && (
                <span className="px-4 py-2 bg-emerald-50 text-emerald-700 text-sm font-bold rounded-full border border-emerald-200">
                  Save ₹500/month
                </span>
              )}
            </div>

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plans.map((plan) => {
                const features = typeof plan.features === 'string' 
                  ? JSON.parse(plan.features || '[]') 
                  : (plan.features || []);
                const isPremium = plan.plan_name === 'premium';
                const isAdvanced = plan.plan_name === 'advanced';
                const isCurrentPlan = currentSubscription?.plan_name === plan.plan_name;
                const isProcessing = processingPlan === plan.plan_name;
                const displayPrice = getDisplayPrice(plan, billingPeriod);
                const paymentAmount = getPaymentAmount(plan, billingPeriod);
                
                // Get credit plan info for this subscription plan
                const creditPlan = plan.ai_credit_plan_id 
                  ? creditPlans.find(cp => cp.id === plan.ai_credit_plan_id)
                  : null;
                const creditsIncluded = plan.ai_credits || 0;
                const totalAiReplies = creditPlan && creditsIncluded > 0
                  ? creditsIncluded * creditPlan.per_credit_ai_replies
                  : 0;

                return (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`relative bg-white rounded-2xl border-2 p-6 flex flex-col ${
                      isPremium ? 'border-purple-500 shadow-xl' :
                      isAdvanced ? 'border-gray-900 shadow-xl' :
                      'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {isPremium && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <span className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-5 py-2 rounded-full text-sm font-bold flex items-center gap-2">
                          <Crown size={16} />
                          Premium
                        </span>
                      </div>
                    )}
                    {isAdvanced && !isPremium && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <span className="bg-gray-900 text-white px-5 py-2 rounded-full text-sm font-bold flex items-center gap-2">
                          <Zap size={16} />
                          Most Popular
                        </span>
                      </div>
                    )}

                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold mb-4 text-gray-900">
                        {plan.plan_display_name}
                      </h3>
                      <div className="flex flex-col items-center gap-2">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-5xl font-bold text-gray-900">
                            ₹{displayPrice.toLocaleString()}
                          </span>
                        </div>
                        <span className="text-gray-600 text-base font-semibold">/month</span>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-6 flex-grow">
                      {features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className="mt-0.5 flex-shrink-0">
                            <div className="p-1 bg-emerald-50 rounded-full border border-emerald-200">
                              <Check size={16} className="text-emerald-600" />
                            </div>
                          </div>
                          <span className="text-gray-700 text-sm leading-relaxed font-medium">{feature}</span>
                        </li>
                      ))}
                      <li className="flex items-start gap-3">
                        <div className="mt-0.5 flex-shrink-0">
                          <div className="p-1 bg-emerald-50 rounded-full border border-emerald-200">
                            <Check size={16} className="text-emerald-600" />
                          </div>
                        </div>
                        <span className="text-gray-700 text-sm leading-relaxed font-medium">
                          {plan.max_flows} Flow{plan.max_flows !== 1 ? 's' : ''}
                        </span>
                      </li>
                      {creditsIncluded > 0 && creditPlan && (
                        <li className="flex items-start gap-3">
                          <div className="mt-0.5 flex-shrink-0">
                            <div className="p-1 bg-gradient-to-r from-[#048AAD]/20 to-[#01CC54]/20 rounded-full border border-[#048AAD]/30">
                              <Sparkles size={16} className="text-[#048AAD]" />
                            </div>
                          </div>
                          <div className="flex-1">
                            <span className="text-gray-700 text-sm leading-relaxed font-medium block">
                              {creditsIncluded} AI Credits
                            </span>
                            <span className="text-gray-500 text-xs mt-0.5 block">
                              (1 credit = {creditPlan.per_credit_ai_replies} AI replies) = {totalAiReplies} total AI replies
                            </span>
                          </div>
                        </li>
                      )}
                    </ul>

                    <div className="mt-auto pt-4">
                      <motion.button
                        onClick={() => handleSubscribe(plan)}
                        disabled={isProcessing || isCurrentPlan}
                        whileHover={!isCurrentPlan && !isProcessing ? { scale: 1.02 } : {}}
                        whileTap={!isCurrentPlan && !isProcessing ? { scale: 0.98 } : {}}
                        className={`w-full py-4 rounded-xl font-bold text-white transition-all flex flex-col items-center justify-center gap-1 shadow-md ${
                          isCurrentPlan
                            ? "bg-gray-400 cursor-not-allowed"
                            : isPremium
                            ? "bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
                            : isAdvanced
                            ? "bg-gray-900 hover:bg-gray-800"
                            : "bg-gray-700 hover:bg-gray-800"
                        }`}
                      >
                        {isProcessing ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm">Processing...</span>
                          </>
                        ) : isCurrentPlan ? (
                          <span className="text-base">Current Plan</span>
                        ) : (
                          <>
                            <span className="text-base flex items-center gap-2">
                              Subscribe Now
                              <ArrowRight size={18} />
                            </span>
                            <span className="text-sm opacity-90">
                              Pay ₹{paymentAmount.toLocaleString()}{billingPeriod === 'yearly' ? '/year' : '/month'}
                            </span>
                          </>
                        )}
                      </motion.button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionInline;

